import pixelsApi from 'api/requests/pixels'
import Button from 'components/_UI/Button'
import Fieldset from 'components/_UI/Fieldset'
import Loader from 'components/_UI/Loader'
import Modal from 'components/_UI/Modal'
import Switch from 'components/_UI/Switch'
import Text from 'components/_UI/Text'
import { useEvent } from 'context/events/Event'
import fieldValidations from 'helpers/fieldValidations'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Select from 'react-select'
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import fonts from 'theme/fonts'

const platformOptions = [
    { label: 'Meta (Facebook)', value: 'meta' },
    { label: 'Google', value: 'google' },
    { label: 'TikTok', value: 'tiktok' },
]

function PixelForm({ visible, setVisible, onSuccess, edit = false }) {
    const { event } = useEvent()
    const [loading, setLoading] = useState(false)
    const [active, setActive] = useState(edit ? edit.is_active : true)
    const [selectedPlatform, setSelectedPlatform] = useState(
        edit ? platformOptions.find(o => o.value === edit.platform) : null
    )
    const [platformError, setPlatformError] = useState(false)
    const [selectedEvents, setSelectedEvents] = useState(edit ? (edit.tracking_events || []) : [])
    const [trackingEvents, setTrackingEvents] = useState([])
    const [loadingEvents, setLoadingEvents] = useState(true)

    useEffect(() => {
        pixelsApi.getTrackingEvents()
            .then(({ data }) => setTrackingEvents(data?.data || []))
            .catch(() => {})
            .finally(() => setLoadingEvents(false))
    }, [])

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            pixel_id: edit?.pixel_id || '',
        }
    })

    function toggleEvent(key) {
        setSelectedEvents(prev =>
            prev.includes(key) ? prev.filter(e => e !== key) : [...prev, key]
        )
    }

    function createPayload(content) {
        return {
            platform: selectedPlatform?.value,
            pixel_id: content.pixel_id,
            is_active: active,
            tracking_events: selectedEvents,
        }
    }

    async function onSubmit(content) {
        if (!selectedPlatform) {
            setPlatformError(true)
            return
        }
        setPlatformError(false)

        setLoading(true)
        const payload = createPayload(content)

        try {
            if (edit) {
                await pixelsApi.update(event.slug, edit.id, payload)
                onSuccess('editPixel')
            } else {
                await pixelsApi.create(event.slug, payload)
                onSuccess('createPixel')
            }
        } catch (error) {
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={visible} onClose={() => setVisible(false)}>
            <Text name='highlight'>{!edit ? 'Adicionar' : 'Editar'} pixel</Text>

            {loadingEvents ? (
                <Column marginTop='20px'>
                    <Loader />
                </Column>
            ) : (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Column marginTop='10px'>
                        <Text name='small' color={colors.nightDark} type='bold' textAlign='left'>Plataforma *</Text>
                        <CustomSelect>
                            <Select
                                placeholder='Selecione a plataforma'
                                value={selectedPlatform}
                                options={platformOptions}
                                onChange={(e) => {
                                    setSelectedPlatform(e)
                                    setPlatformError(false)
                                }}
                            />
                        </CustomSelect>
                        {platformError && (
                            <Column>
                                <Text color={colors.danger} name='small'>O campo plataforma é obrigatório</Text>
                            </Column>
                        )}
                    </Column>

                    <Column marginTop='10px'>
                        <Fieldset
                            label='ID do Pixel *'
                            placeholder='Ex: 123456789'
                            register={register}
                            name='pixel_id'
                            layout='squired'
                            validations={fieldValidations.name('ID do pixel', 3, 100)}
                        />
                        {errors?.pixel_id?.message && (
                            <Column>
                                <Text color={colors.danger} name='small'>{errors.pixel_id.message}</Text>
                            </Column>
                        )}
                    </Column>

                    <Column marginTop='10px'>
                        <Switch
                            label='Pixel ativo'
                            labelSize={fonts.sizes.small}
                            onChange={(e) => setActive(e)}
                            checked={active}
                        />
                    </Column>

                    <Column marginTop='10px'>
                        <Text name='small' color={colors.nightDark} type='bold' textAlign='left'>
                            Eventos de conversão
                        </Text>
                        {trackingEvents.map(evt => (
                            <CheckboxRow key={evt.key}>
                                <input
                                    type='checkbox'
                                    id={`evt-${evt.key}`}
                                    checked={selectedEvents.includes(evt.key)}
                                    onChange={() => toggleEvent(evt.key)}
                                />
                                <CheckboxLabel htmlFor={`evt-${evt.key}`}>{evt.label}</CheckboxLabel>
                            </CheckboxRow>
                        ))}
                    </Column>

                    {loading ? (
                        <Loader />
                    ) : (
                        <ContentButton>
                            <Button
                                variant='light'
                                category='roundedBorded'
                                onClick={() => setVisible(false)}
                            >
                                Cancelar
                            </Button>
                            <Button variant='featured' type='submit'>
                                {!edit ? 'Adicionar' : 'Atualizar'}
                            </Button>
                        </ContentButton>
                    )}
                </Form>
            )}
        </Modal>
    )
}

export default PixelForm

const Form = styled.form`
    width: 100%;
`

const ContentButton = styled(Column)`
    margin-top: 15px;
    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`

const CustomSelect = styled.div`
    width: 100%;

    & > div > div {
        text-align: left;
        font-size: ${fonts.sizes.small};
    }
`

const CheckboxRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 6px;

    input[type='checkbox'] {
        width: 18px;
        height: 18px;
        cursor: pointer;
    }
`

const CheckboxLabel = styled.label`
    margin-left: 10px;
    font-size: 14px;
    color: ${colors.nightDark};
    cursor: pointer;
`
